import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../../../services/api';
import { ArrowLeft, Plus, Edit2, Trash2, Briefcase, DollarSign } from 'lucide-react';
import '../../../styles/admin/ERPServices.css';

const ERPServices = () => {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [products, setProducts] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [formData, setFormData] = useState({
    nom: '',
    description: '',
    department_id: '',
    type_tarification: 'pi2',
    // Nouveaux champs basés sur liste de prix 2025
    cout_produit_par_1000_pi2: '',
    prix_vente_par_1000_pi2: '',
    temps_application_minutes: '',
    temps_preparation_minutes: '',
    temps_transport_minutes: '',
    // Champs legacy
    prix_base: '',
    estimation_temps_minutes: '',
    nombre_employes_defaut: 1,
    produits_necessaires: [],
    regles_employes: null
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [servicesRes, departmentsRes, productsRes, configRes] = await Promise.all([
        api.get('/api/erp/services?actif_only=false'),
        api.get('/api/erp/departments?actif_only=true'),
        api.get('/api/erp/products'),
        api.get('/api/erp/config')
      ]);
      
      setServices(servicesRes.data.services);
      setDepartments(departmentsRes.data.departments);
      setProducts(productsRes.data.products);
      setConfig(configRes.data.config);
    } catch (error) {
      if (error.response?.status === 401) {
        
        navigate('/auth/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateService = () => {
    setEditingService(null);
    setFormData({
      nom: '',
      description: '',
      department_id: '',
      type_tarification: 'pi2',
      cout_produit_par_1000_pi2: '',
      prix_vente_par_1000_pi2: '',
      temps_application_minutes: '',
      temps_preparation_minutes: '',
      temps_transport_minutes: '',
      prix_base: '',
      estimation_temps_minutes: '',
      nombre_employes_defaut: 1,
      produits_necessaires: [],
      regles_employes: null
    });
    setShowModal(true);
  };

  const handleEditService = (service) => {
    setEditingService(service);
    setFormData({
      nom: service.nom,
      description: service.description || '',
      department_id: service.department_id || '',
      type_tarification: service.type_tarification,
      cout_produit_par_1000_pi2: service.cout_produit_par_1000_pi2 || '',
      prix_vente_par_1000_pi2: service.prix_vente_par_1000_pi2 || '',
      temps_application_minutes: service.temps_application_minutes || '',
      temps_preparation_minutes: service.temps_preparation_minutes || '',
      temps_transport_minutes: service.temps_transport_minutes || '',
      prix_base: service.prix_base || '',
      estimation_temps_minutes: service.estimation_temps_minutes,
      nombre_employes_defaut: service.nombre_employes_defaut,
      produits_necessaires: service.produits_necessaires || [],
      regles_employes: service.regles_employes
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const serviceData = {
        ...formData,
        prix_base: parseFloat(formData.prix_base) || 0,
        estimation_temps_minutes: parseInt(formData.estimation_temps_minutes),
        nombre_employes_defaut: parseInt(formData.nombre_employes_defaut)
      };

      if (editingService) {
        await api.put(
          `/api/erp/services/${editingService.id}`,
          serviceData
        );
      } else {
        await api.post(
          '/api/erp/services',
          serviceData
        );
      }

      setShowModal(false);
      fetchData();
    } catch (error) {
      console.error('Erreur sauvegarde service:', error);
      alert(error.response?.data?.detail || 'Erreur lors de la sauvegarde du service');
    }
  };

  const handleDeleteService = async (serviceId) => {
    if (!window.confirm('Voulez-vous vraiment désactiver ce service?')) return;
    
    try {
      await api.delete(`/api/erp/services/${serviceId}`);
      fetchData();
    } catch (error) {
      console.error('Erreur suppression:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const toggleProductSelection = (productId) => {
    const isSelected = formData.produits_necessaires.some(p => p.product_id === productId);
    
    if (isSelected) {
      setFormData({
        ...formData,
        produits_necessaires: formData.produits_necessaires.filter(p => p.product_id !== productId)
      });
    } else {
      setFormData({
        ...formData,
        produits_necessaires: [...formData.produits_necessaires, { product_id: productId, obligatoire: true }]
      });
    }
  };

  const getTypeLabel = (type) => {
    switch(type) {
      case 'pi2': return 'Pi² (Superficie)';
      case 'horaire': return 'Horaire';
      case 'lineaire': return 'Pieds linéaires';
      default: return type;
    }
  };

  if (loading) {
    return <div className="admin-loading">Chargement...</div>;
  }

  return (
    <div className="erp-services-page">
      <div className="erp-services-header">
        <div className="erp-services-header-content">
          <Link to="/admin/erp" className="erp-back-link">
            <ArrowLeft size={20} />
            Retour au dashboard
          </Link>
          <div className="header-title-section">
            <h1><Briefcase size={32} /> Services</h1>
            <p>Gérer les services et tarifications</p>
          </div>
        </div>
      </div>

      <div className="erp-services-content">
        {/* Toolbar */}
        <div className="services-toolbar">
          <div className="toolbar-info">
            <span>{services.filter(s => s.actif).length} service(s) actif(s)</span>
            <Link to="/admin/erp/packages" style={{marginLeft: '1rem', color: '#F57C00', fontWeight: '600'}}>
              📦 Gérer les forfaits
            </Link>
          </div>
          <button className="btn-primary" onClick={handleCreateService}>
            <Plus size={20} />
            Nouveau service
          </button>
        </div>

        {/* Services List */}
        <div className="services-list">
          {services.length === 0 ? (
            <div className="empty-state">
              <Briefcase size={48} color="#ccc" />
              <p>Aucun service créé</p>
              <button className="btn-primary" onClick={handleCreateService}>
                <Plus size={20} />
                Créer le premier service
              </button>
            </div>
          ) : (
            services.map(service => (
              <div key={service.id} className={`service-card ${!service.actif ? 'inactive' : ''}`}>
                <div className="service-card-header">
                  <div>
                    <h3>{service.nom}</h3>
                    <div style={{display: 'flex', gap: '0.5rem', alignItems: 'center', marginTop: '0.5rem'}}>
                      {service.department_id && departments.find(d => d.id === service.department_id) && (
                        <span 
                          className="department-badge" 
                          style={{
                            background: departments.find(d => d.id === service.department_id)?.couleur || '#01500A',
                            color: 'white',
                            padding: '0.25rem 0.75rem',
                            borderRadius: '6px',
                            fontSize: '0.75rem',
                            fontWeight: '600'
                          }}
                        >
                          {departments.find(d => d.id === service.department_id)?.nom}
                        </span>
                      )}
                      <span className="service-type-badge">{getTypeLabel(service.type_tarification)}</span>
                    </div>
                  </div>
                  <div className="service-actions">
                    <button
                      className="btn-icon"
                      onClick={() => handleEditService(service)}
                      title="Modifier"
                    >
                      <Edit2 size={18} />
                    </button>
                    {service.actif && (
                      <button
                        className="btn-icon danger"
                        onClick={() => handleDeleteService(service.id)}
                        title="Désactiver"
                      >
                        <Trash2 size={18} />
                      </button>
                    )}
                  </div>
                </div>

                {service.description && (
                  <p className="service-description">{service.description}</p>
                )}

                <div className="service-details-grid">
                  {service.prix_base > 0 && (
                    <div className="service-detail">
                      <DollarSign size={16} />
                      <span>Prix de base: {service.prix_base.toFixed(2)} $</span>
                    </div>
                  )}
                  <div className="service-detail">
                    <span>⏱️ Temps estimé: {service.estimation_temps_minutes} min</span>
                  </div>
                  <div className="service-detail">
                    <span>👥 Employés: {service.nombre_employes_defaut}</span>
                  </div>
                  <div className="service-detail">
                    <span>💵 Taux horaire: {service.taux_horaire_employe} $/h</span>
                  </div>
                </div>

                {service.produits_necessaires && service.produits_necessaires.length > 0 && (
                  <div className="service-products">
                    <strong>Produits nécessaires:</strong>
                    <div className="products-tags">
                      {service.produits_necessaires.map((prod, idx) => {
                        const product = products.find(p => p.id === prod.product_id);
                        return product ? (
                          <span key={idx} className="product-tag">
                            {product.nom}
                          </span>
                        ) : null;
                      })}
                    </div>
                  </div>
                )}

                {!service.actif && (
                  <div className="service-inactive-badge">Désactivé</div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingService ? 'Modifier le service' : 'Nouveau service'}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            
            <form onSubmit={handleSubmit} className="service-form">
              {/* Basic Info */}
              <div className="form-section">
                <h3>📋 Informations de base</h3>
                
                <div className="form-group">
                  <label>Nom du service *</label>
                  <input
                    type="text"
                    required
                    value={formData.nom}
                    onChange={(e) => setFormData({...formData, nom: e.target.value})}
                    placeholder="Ex: Fertilisation complète"
                  />
                </div>

                <div className="form-group">
                  <label>Département *</label>
                  <select
                    value={formData.department_id}
                    onChange={(e) => setFormData({...formData, department_id: e.target.value})}
                    required
                  >
                    <option value="">-- Sélectionner un département --</option>
                    {departments.map(dept => (
                      <option key={dept.id} value={dept.id}>
                        {dept.nom}
                      </option>
                    ))}
                  </select>
                  {departments.length === 0 && (
                    <small style={{color: '#C62828'}}>
                      Aucun département disponible. <Link to="/admin/erp/departments" style={{color: '#01500A'}}>Créer un département</Link>
                    </small>
                  )}
                </div>

                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    rows="2"
                    placeholder="Description du service..."
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Type de tarification *</label>
                    <select
                      value={formData.type_tarification}
                      onChange={(e) => setFormData({...formData, type_tarification: e.target.value})}
                      required
                    >
                      <option value="pi2">Pi² (Superficie)</option>
                      <option value="horaire">Horaire</option>
                      <option value="lineaire">Pieds linéaires</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Prix de base (optionnel)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.prix_base}
                      onChange={(e) => setFormData({...formData, prix_base: e.target.value})}
                      placeholder="0.00"
                    />
                  </div>
                </div>
              </div>

              {/* Tarification 2025 (basée sur liste de prix) */}
              <div className="form-section highlight-section">
                <h3>💰 Tarification par 1000 pi² (Liste de prix 2025)</h3>
                <p className="section-note">Ces valeurs permettent un calcul automatique précis basé sur la superficie du terrain.</p>
                
                <div className="form-row">
                  <div className="form-group">
                    <label>Coût produit / 1000 pi² ($)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.cout_produit_par_1000_pi2}
                      onChange={(e) => setFormData({...formData, cout_produit_par_1000_pi2: e.target.value})}
                      placeholder="10.50"
                    />
                    <span className="form-hint">Ex: 10.50 $ pour fertilisation</span>
                  </div>

                  <div className="form-group">
                    <label>Prix de vente / 1000 pi² ($)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.prix_vente_par_1000_pi2}
                      onChange={(e) => setFormData({...formData, prix_vente_par_1000_pi2: e.target.value})}
                      placeholder="26.76"
                    />
                    <span className="form-hint">Ex: 26.76 $ (incluant marge)</span>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Temps application (min / 1000 pi²)</label>
                    <input
                      type="number"
                      step="0.1"
                      value={formData.temps_application_minutes}
                      onChange={(e) => setFormData({...formData, temps_application_minutes: e.target.value})}
                      placeholder="12"
                    />
                  </div>

                  <div className="form-group">
                    <label>Temps préparation (min)</label>
                    <input
                      type="number"
                      step="0.1"
                      value={formData.temps_preparation_minutes}
                      onChange={(e) => setFormData({...formData, temps_preparation_minutes: e.target.value})}
                      placeholder="5"
                    />
                  </div>

                  <div className="form-group">
                    <label>Temps transport (min)</label>
                    <input
                      type="number"
                      step="0.1"
                      value={formData.temps_transport_minutes}
                      onChange={(e) => setFormData({...formData, temps_transport_minutes: e.target.value})}
                      placeholder="18"
                    />
                  </div>
                </div>

                {formData.prix_vente_par_1000_pi2 && formData.cout_produit_par_1000_pi2 && (
                  <div className="margin-preview">
                    <strong>Marge brute:</strong> {
                      ((parseFloat(formData.prix_vente_par_1000_pi2) - parseFloat(formData.cout_produit_par_1000_pi2)) / 
                      parseFloat(formData.prix_vente_par_1000_pi2) * 100).toFixed(1)
                    }%
                  </div>
                )}
              </div>

              {/* Labor (Legacy) */}
              <div className="form-section">
                <h3>👷 Main-d'œuvre</h3>
                
                <div className="form-row">
                  <div className="form-group">
                    <label>Temps estimé (minutes) *</label>
                    <input
                      type="number"
                      required
                      value={formData.estimation_temps_minutes}
                      onChange={(e) => setFormData({...formData, estimation_temps_minutes: e.target.value})}
                      placeholder="60"
                    />
                    <span className="form-hint">Pour 1000 pi² si type = superficie</span>
                  </div>

                  <div className="form-group">
                    <label>Nombre d'employés par défaut *</label>
                    <input
                      type="number"
                      required
                      min="1"
                      value={formData.nombre_employes_defaut}
                      onChange={(e) => setFormData({...formData, nombre_employes_defaut: e.target.value})}
                    />
                  </div>
                </div>

                <div className="config-info">
                  ℹ️ Taux horaire actuel: {config?.taux_horaire_employe || 80} $/h • Marge: {config?.marge_profit_pourcent || 45}%
                </div>
              </div>

              {/* Products */}
              {formData.type_tarification === 'pi2' && products.length > 0 && (
                <div className="form-section">
                  <h3>📦 Produits nécessaires</h3>
                  <p className="section-description">Sélectionnez les produits qui seront appliqués</p>
                  
                  <div className="products-selection">
                    {products.map(product => (
                      <label key={product.id} className="product-checkbox">
                        <input
                          type="checkbox"
                          checked={formData.produits_necessaires.some(p => p.product_id === product.id)}
                          onChange={() => toggleProductSelection(product.id)}
                        />
                        <div className="product-checkbox-label">
                          <strong>{product.nom}</strong>
                          <span>{product.taux_application} lbs/1000 pi² • {product.format_sac} lbs/sac • {product.prix_achat} $/sac</span>
                        </div>
                      </label>
                    ))}
                  </div>

                  {products.length === 0 && (
                    <div className="empty-products">
                      <p>Aucun produit disponible. <Link to="/admin/erp/inventory">Créer des produits</Link></p>
                    </div>
                  )}
                </div>
              )}

              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowModal(false)}>
                  Annuler
                </button>
                <button type="submit" className="btn-primary">
                  {editingService ? 'Mettre à jour' : 'Créer le service'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ERPServices;
